import { useEffect, useMemo, useState } from 'react'

import { KeyboardArrowRight } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import { marked } from 'marked'

import Row from 'src/components/Row/Row'

type ChildComponentProps = {
  setCount: React.Dispatch<React.SetStateAction<number>>
  count?: number
}

type Props = {
  title: string
  isOpen: boolean
  children?: React.ReactElement<ChildComponentProps>
  count?: number
  plainText?: string
  markdown?: boolean
  objectChip?: React.ReactNode
  sx?: any
}

const SidebarSectionExpand: React.FC<Props> = ({
  title,
  isOpen = false,
  count,
  plainText,
  children,
  markdown = false,
  sx = {},
  objectChip,
}) => {
  const [open, setOpen] = useState(isOpen)
  const [adjustableCount, setAdjustableCount] = useState(count)

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    setAdjustableCount(count)
  }, [count])

  const htmlRenderer = useMemo(() => new marked.Renderer(), [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        pb: 2,
        width: '100%',
        ...sx,
      }}
    >
      <Box
        sx={{ px: 2, width: 'calc(100%)' }}
        className="sidebar-section-expand-container"
      >
        <Row
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            pt: 2,
            width: 'calc(100%)',
            cursor: 'pointer',
            justifyContent: 'space-between',
          }}
          className="sidebar-section-expand-title-row"
        >
          <Row sx={{ gap: 1 }}>
            <Typography
              variant="h3"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: '2px',
                lineHeight: '22px',
                flexShrink: 0,
              }}
            >
              {title}
            </Typography>
          </Row>
          <Row>
            {objectChip}
            {typeof adjustableCount === 'number' && adjustableCount > 0 && (
              <Row
                sx={{
                  borderRadius: '50px',
                  backgroundColor: (theme) => theme.palette.divider,
                  width: '24px',
                  height: '24px',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                >
                  {`${
                    typeof adjustableCount === 'number'
                      ? `${adjustableCount}`
                      : ''
                  }`}
                </Typography>
              </Row>
            )}
            <KeyboardArrowRight
              sx={{
                transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'all 0.25s linear',
              }}
            />
          </Row>
        </Row>
        <Box
          sx={{
            maxHeight: open ? 'auto' : '0px',
            transition: 'all 0.25s ease',
            width: '100%',
            overflow: 'hidden',

            '& p': {
              transition: 'all 0.25s linear',
              opacity: open ? 1 : 0,
              pt: open ? '8px' : '0px',

              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '150%',
              letterSpacing: '-0.10px',
            },
            '& ul': {
              listStyle: 'disc outside none',
              pl: 3,
              margin: '8px 0',
            },
            '& li': {
              display: 'list-item',
              marginBottom: '4px',
              ml: 1,

              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '150%',
              letterSpacing: '-0.10px',
            },
          }}
        >
          {plainText && !children && (
            <>
              {markdown ? (
                <Box
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      marked(plainText, { renderer: htmlRenderer })
                    ),
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {plainText}
                </Typography>
              )}
            </>
          )}
          {children && (
            <Box
              sx={{
                transition: 'all 0.25s linear',
                opacity: open ? 1 : 0,
                mt: open ? 1 : '0px',
                pt: open ? 1 : '0px',
                width: '100%',
                height: '100%',
              }}
            >
              {React.isValidElement(children) &&
                React.cloneElement(children, {
                  setCount: setAdjustableCount,
                  count: adjustableCount,
                })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default SidebarSectionExpand
